import { useEffect, useState, useCallback, useRef } from "react";
import { CreditCard, Plus, Trash2, Upload, Download, Eye, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, Pencil } from "lucide-react";
// Download déjà importé pour les documents
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import EmployeePicker from "../components/EmployeePicker";
import ExportDialog from "../components/ExportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUTS = {
  valide: { label: "Valide", classes: "bg-emerald-950 text-emerald-400 border-emerald-700" },
  a_recycler: { label: "À renouveler", classes: "bg-amber-950 text-amber-400 border-amber-700" },
  depasse: { label: "Expirée", classes: "bg-red-950 text-red-400 border-red-700" },
  inconnu: { label: "Inconnu", classes: "bg-gray-800 text-gray-300 border-gray-600" },
};

export function CarteProBadge() {
  return (
    <span className="inline-flex items-center rounded-full border border-orange-700 bg-orange-950 px-2.5 py-0.5 text-xs font-bold text-orange-400" data-testid="carte-pro-badge">
      Carte Pro
    </span>
  );
}

function Jours({ jours }) {
  if (jours === null || jours === undefined) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = jours < 0 ? "text-red-400" : jours <= 365 ? "text-amber-400" : "text-muted-foreground";
  const ans = Math.floor(Math.abs(jours) / 365);
  const mois = Math.round((Math.abs(jours) % 365) / 30.44);
  const txt = jours < 0
    ? `expirée depuis ${ans > 0 ? `${ans} an${ans > 1 ? "s" : ""} ` : ""}${mois > 0 ? `${mois} mois` : ""}`.trim()
    : `${ans > 0 ? `${ans} an${ans > 1 ? "s" : ""} ` : ""}${mois > 0 ? `${mois} mois` : `${Math.abs(jours)} j`}`.trim();
  return <span className={`text-xs font-mono ${cls}`}>{txt}</span>;
}

const EMPTY = { nub: "", date_delivrance: "", date_validite: "" };

export default function CartesPro() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "carte_pro", "modifier");
  const [agents, setAgents] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageAgent, setManageAgent] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ emp_id: "", ...EMPTY });
  const [editForm, setEditForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/cartes-pro").then((r) => setAgents(r.data.agents)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
  }, [load]);

  const kpis = agents.reduce((acc, a) => {
    const st = a.carte_pro?.statut;
    if (st) acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const openPreview = async (c) => {
    try {
      const r = await api.get(`/certificats/${c.id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), filename: c.filename });
    } catch (e) {
      toast.error(formatApiError(e, "Aperçu impossible"));
    }
  };

  const download = (c) => downloadFile(`/certificats/${c.id}/download`, c.filename).catch(() => toast.error("Téléchargement impossible"));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !manageAgent) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/employees/${manageAgent.id}/certificats?categorie=carte_pro`, form);
      toast.success("Carte professionnelle téléversée — analyse en cours");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Échec du téléversement"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeCert = async (c) => {
    if (!window.confirm(`Supprimer "${c.filename}" ?`)) return;
    try {
      await api.delete(`/certificats/${c.id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const applyAnalyse = async (c) => {
    try {
      await api.post(`/certificats/${c.id}/appliquer`);
      toast.success("Carte professionnelle mise à jour depuis le document");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openManage = (a) => {
    setManageAgent(a);
    setEditing(false);
    setEditForm({ nub: a.carte_pro?.nub || "", date_delivrance: a.carte_pro?.date_delivrance || "", date_validite: a.carte_pro?.date_validite || "" });
  };

  const saveCard = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${manageAgent.id}/carte-pro`, {
        nub: editForm.nub || null,
        date_delivrance: editForm.date_delivrance || null,
        date_validite: editForm.date_validite || null,
      });
      toast.success("Carte professionnelle mise à jour");
      setEditing(false);
      load();
      setManageAgent(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeCard = async () => {
    if (!window.confirm(`Retirer la carte professionnelle de ${manageAgent.prenom} ${manageAgent.nom} ?`)) return;
    try {
      await api.delete(`/employees/${manageAgent.id}/carte-pro`);
      toast.success("Carte professionnelle retirée");
      setManageAgent(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addAgent = async () => {
    if (!addForm.emp_id) {
      toast.error("Choisissez un salarié");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/employees/${addForm.emp_id}/carte-pro`, {
        nub: addForm.nub || null,
        date_delivrance: addForm.date_delivrance || null,
        date_validite: addForm.date_validite || null,
      });
      toast.success("Carte professionnelle ajoutée");
      setAddOpen(false);
      setAddForm({ emp_id: "", ...EMPTY });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const currentAgent = manageAgent ? agents.find((a) => a.id === manageAgent.id) || manageAgent : null;

  return (
    <div className="space-y-6" data-testid="cartes-pro-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-orange-500 mb-1">Agents de sécurité</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <CreditCard className="h-8 w-8 text-orange-500" /> Cartes <span className="text-orange-500">Pro</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Validité 5 ans · rappel automatique 1 an avant expiration</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportDialog type="carte_pro" filename="export_cartes_pro.xlsx" testid="export-cartes-pro-button" />
          {!readOnly && (
            <Button onClick={() => setAddOpen(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="add-carte-pro-button">
              <Plus className="h-4 w-4 mr-2" /> Ajouter une carte pro
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Cartes suivies", value: agents.length, color: "text-orange-400", testid: "cartepro-kpi-agents" },
          { label: "Valides", value: kpis.valide || 0, color: "text-emerald-400", testid: "cartepro-kpi-valides" },
          { label: "À renouveler (< 1 an)", value: kpis.a_recycler || 0, color: "text-amber-400", testid: "cartepro-kpi-renouveler" },
          { label: "Expirées", value: kpis.depasse || 0, color: "text-red-400", testid: "cartepro-kpi-expirees" },
        ].map((k) => (
          <div key={k.testid} className="rounded-xl border border-orange-900/50 bg-orange-950/20 p-4" data-testid={k.testid}>
            <p className={`text-2xl font-extrabold font-mono ${k.color}`}>{k.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cartes-pro-table">
            <thead>
              <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3"><span className="text-orange-400">N° d'identification</span></th>
                <th className="px-4 py-3">Validité du titre</th>
                <th className="px-4 py-3">Délai</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>}
              {!loading && agents.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="cartes-pro-empty">Aucune carte professionnelle enregistrée.</td></tr>}
              {!loading && agents.map((a) => (
                <tr key={a.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`cartepro-row-${a.id}`}>
                  <td className="px-4 py-3"><span className="font-medium">{a.nom}</span> {a.prenom}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.service}</td>
                  <td className="px-4 py-3 font-mono text-xs text-orange-400 font-semibold">{a.carte_pro?.nub || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{a.carte_pro?.date_validite || "—"}</td>
                  <td className="px-4 py-3"><Jours jours={a.carte_pro?.jours_restants} /></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[a.carte_pro?.statut || "inconnu"].classes}`}>
                      {STATUTS[a.carte_pro?.statut || "inconnu"].label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" title="Gérer" onClick={() => openManage(a)} data-testid={`cartepro-manage-${a.id}`}>
                      <FileText className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-2" data-testid="cartepro-cards">
        {loading && <p className="py-8 text-center text-muted-foreground">Chargement...</p>}
        {!loading && agents.length === 0 && <p className="py-8 text-center text-muted-foreground">Aucune carte professionnelle enregistrée.</p>}
        {!loading && agents.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid={`cartepro-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold">{a.nom} {a.prenom}</p>
                <p className="text-xs text-muted-foreground">{a.service}</p>
              </div>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[a.carte_pro?.statut || "inconnu"].classes}`}>
                {STATUTS[a.carte_pro?.statut || "inconnu"].label}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-muted-foreground">
              <span>N° <span className="text-orange-400 font-semibold">{a.carte_pro?.nub || "—"}</span></span>
              <span>Validité : {a.carte_pro?.date_validite || "—"}</span>
              <Jours jours={a.carte_pro?.jours_restants} />
            </div>
            <Button variant="outline" size="sm" className="w-full h-10" onClick={() => openManage(a)} data-testid={`cartepro-manage-m-${a.id}`}>
              <FileText className="h-4 w-4 mr-1.5" /> Gérer la carte
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!manageAgent} onOpenChange={(o) => !o && setManageAgent(null)}>
        <DialogContent className="max-w-2xl" data-testid="cartepro-manage-dialog">
          {currentAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-orange-500" /> {currentAgent.prenom} {currentAgent.nom}
                </DialogTitle>
                <DialogDescription>{currentAgent.service} — carte professionnelle et documents PDF</DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Carte professionnelle</Label>
                  {!readOnly && !editing && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="cartepro-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifier</Button>
                      <Button size="sm" variant="ghost" className="hover:text-red-400" onClick={removeCard} data-testid="cartepro-remove-button"><Trash2 className="h-3.5 w-3.5 mr-1" /> Retirer</Button>
                    </div>
                  )}
                </div>
                {!editing ? (
                  <div className="flex items-center gap-3 flex-wrap rounded-lg border border-border px-3 py-2.5" data-testid="cartepro-card-view">
                    <CarteProBadge />
                    <span className="text-xs font-mono">N° {currentAgent.carte_pro?.nub || "—"}</span>
                    <span className="text-xs font-mono">validité : {currentAgent.carte_pro?.date_validite || "—"}</span>
                    <Jours jours={currentAgent.carte_pro?.jours_restants} />
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
                    <div className="space-y-1">
                      <Label className="text-xs">N° d'identification (NUB)</Label>
                      <Input value={editForm.nub} onChange={(e) => setEditForm({ ...editForm, nub: e.target.value })} className="bg-background w-36 font-mono" data-testid="cartepro-form-nub" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Délivrance</Label>
                      <Input type="date" value={editForm.date_delivrance} onChange={(e) => setEditForm({ ...editForm, date_delivrance: e.target.value })} className="bg-background w-40" data-testid="cartepro-form-delivrance" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Validité <span className="text-muted-foreground">(auto +5 ans)</span></Label>
                      <Input type="date" value={editForm.date_validite} onChange={(e) => setEditForm({ ...editForm, date_validite: e.target.value })} className="bg-background w-40" data-testid="cartepro-form-validite" />
                    </div>
                    <Button size="sm" onClick={saveCard} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="cartepro-form-submit">Enregistrer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Documents PDF</Label>
                {!readOnly && (
                  <div>
                    <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={upload} data-testid="cartepro-cert-input" />
                    <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="cartepro-cert-upload">
                      {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                      Téléverser la carte pro (PDF)
                    </Button>
                  </div>
                )}
                {currentAgent.certificats?.length === 0 && <p className="text-xs text-muted-foreground">Aucun document téléversé.</p>}
                {currentAgent.certificats?.map((c) => (
                  <div key={c.id} className="rounded-lg border border-border" data-testid={`cartepro-cert-${c.id}`}>
                    <div className="flex items-center justify-between px-3 py-2">
                      <button onClick={() => openPreview(c)} className="flex items-center gap-2 text-sm min-w-0 text-left hover:text-orange-400 transition-colors">
                        <FileText className="h-4 w-4 text-orange-400 shrink-0" />
                        <span className="truncate hover:underline underline-offset-2">{c.filename}</span>
                        <span className="text-xs text-muted-foreground font-mono shrink-0">{c.created_at?.slice(0, 10)}</span>
                      </button>
                      <div className="flex items-center shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPreview(c)} data-testid={`cartepro-cert-view-${c.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => download(c)} data-testid={`cartepro-cert-dl-${c.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        {!readOnly && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeCert(c)} data-testid={`cartepro-cert-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </div>
                    {c.analyse?.statut === "en_cours" && (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground px-3 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-orange-400" /> Analyse automatique en cours...</p>
                    )}
                    {c.analyse?.statut === "termine" && (
                      <div className="mx-3 mb-2 rounded-lg border border-orange-900/50 bg-orange-950/20 p-3 space-y-1.5" data-testid={`cartepro-analysis-${c.id}`}>
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-orange-400"><Sparkles className="h-3.5 w-3.5" /> Analyse automatique (confiance : {c.analyse.confiance})</p>
                        <p className="text-xs">Détecté : <span className="font-medium">{c.analyse.prenom || "?"} {c.analyse.nom || "?"}</span>
                          {c.analyse.numero_identification && <> · N° <span className="font-mono text-orange-400 font-semibold">{c.analyse.numero_identification}</span></>}
                          {c.analyse.date_formation && <> · délivrance <span className="font-mono">{c.analyse.date_formation}</span></>}
                          {c.analyse.date_validite && <> · validité <span className="font-mono">{c.analyse.date_validite}</span></>}
                        </p>
                        {!c.analyse.correspondance && (
                          <p className="flex items-center gap-1.5 text-xs text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> Le nom détecté ne correspond pas à cet agent.</p>
                        )}
                        {c.analyse.appliquee ? (
                          <p className="flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Données appliquées à la carte</p>
                        ) : !readOnly && (c.analyse.date_validite || c.analyse.date_formation || c.analyse.numero_identification) && (
                          <Button size="sm" variant="outline" onClick={() => applyAnalyse(c)} data-testid={`cartepro-apply-${c.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Appliquer les données
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="cartepro-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-orange-500" /> Ajouter une carte professionnelle</DialogTitle>
            <DialogDescription>Associez une carte professionnelle à un salarié existant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Salarié *</Label>
              <EmployeePicker employees={allEmployees.filter((e) => !e.carte_pro)} value={addForm.emp_id}
                onChange={(v) => setAddForm({ ...addForm, emp_id: v })} testid="cartepro-add-employee" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>N° identif.</Label>
                <Input value={addForm.nub} onChange={(e) => setAddForm({ ...addForm, nub: e.target.value })} className="bg-background font-mono" data-testid="cartepro-add-nub" />
              </div>
              <div className="space-y-2">
                <Label>Délivrance</Label>
                <Input type="date" value={addForm.date_delivrance} onChange={(e) => setAddForm({ ...addForm, date_delivrance: e.target.value })} className="bg-background" data-testid="cartepro-add-delivrance" />
              </div>
              <div className="space-y-2">
                <Label>Validité</Label>
                <Input type="date" value={addForm.date_validite} onChange={(e) => setAddForm({ ...addForm, date_validite: e.target.value })} className="bg-background" data-testid="cartepro-add-validite" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={addAgent} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="cartepro-add-submit">
              {saving ? "Enregistrement..." : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="cartepro-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-orange-400" /> {preview?.filename}</DialogTitle>
            <DialogDescription>Aperçu du document PDF</DialogDescription>
          </DialogHeader>
          {preview && <iframe src={preview.url} title={preview.filename} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="cartepro-preview-frame" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
